import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Parse stored user
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setToken(storedToken);
          // Use the configured api instance so withCredentials and interceptors apply
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Verify token is still valid by fetching user
          await fetchUser();
        } catch (error) {
          console.error('Error initializing auth:', error);
          logout();
        }
      } else {
        setLoading(false);
      }
    };
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get(`/auth/me`);
      // Support multiple response shapes returned by different backends
      // e.g. { user: {...} } or { data: { user: {...} } } or { data: {...} }
      const respData = response.data || {};
      const fetchedUser = respData.user || respData.data?.user || respData.data || null;
      if (fetchedUser) {
        setUser(fetchedUser);
        localStorage.setItem('user', JSON.stringify(fetchedUser));
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // If token is expired or invalid, logout
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, role) => {
    try {
      const response = await api.post(`/auth/login`, {
        email,
        password,
        role,
      });
      const resp = response.data || {};

      // If MFA is required, don't set user/token yet
      const mfaRequired = resp.mfaRequired || false;

      if (mfaRequired) {
        return {
          success: true,
          mfaRequired: true,
          email: resp.data?.email,
          mfaType: resp.mfaType
        };
      }

      const token = resp.token || resp.data?.token || resp.data?.auth?.token || null;
      const user = resp.user || resp.data?.user || resp.data || null;

      if (token) {
        localStorage.setItem('token', token);
        setToken(token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        // Save name and email for worker onboarding form auto-fill
        localStorage.setItem('userName', user.name || '');
        localStorage.setItem('userEmail', user.email || '');
        setUser(user);
      }

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  };

  const verifyMFALogin = async (email, token) => {
    try {
      const response = await api.post(`/auth/mfa/login-verify`, { email, token });
      const { token: jwtToken, user } = response.data.data;

      localStorage.setItem('token', jwtToken);
      setToken(jwtToken);
      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      // Save name and email for worker onboarding form auto-fill
      localStorage.setItem('userName', user.name || '');
      localStorage.setItem('userEmail', user.email || '');
      api.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'MFA verification failed',
      };
    }
  };

  const verifyOTPLogin = async (email, otp) => {
    try {
      const response = await api.post(`/auth/login-otp/verify`, { email, otp });
      const { token: jwtToken, user } = response.data.data;

      localStorage.setItem('token', jwtToken);
      setToken(jwtToken);
      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      // Save name and email for worker onboarding form auto-fill
      localStorage.setItem('userName', user.name || '');
      localStorage.setItem('userEmail', user.email || '');
      api.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Verification failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post(`/auth/register`, userData);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
      };
    }
  };

  const logout = async () => {
    try {
      // Attempt to log the logout on server for analytics
      // We don't await strictly or handle errors to ensure client logout is instant
      await api.post('/activity/logout').catch(err => console.warn('Logout logging failed', err));
    } catch (error) {
      // Ignore
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    fetchUser,
    login,
    verifyMFALogin,
    verifyOTPLogin,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
